/**
 * Home — landing page.
 *
 * Signed-in users get the person-first dashboard: a next-meeting prep strip
 * (when a calendar source is connected), a "who are you prepping for?" search
 * over their people, a recent-people rail, and the read surfaces demoted to a
 * quiet secondary row. (Reworked 2026-06-08 from the six-tile surface-first
 * grid — a rep thinks "I've got Phil at 2pm," not "I want a Blueprint.")
 *
 * Signed-out visitors are redirected to sign-in (marketing lives on
 * cersosimo.co; pro.cersosimo.co is app-only).
 */

import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { Wordmark } from "../components/index.js";
import { useSession } from "../lib/useSession.js";
import { canAccess } from "../lib/feature-gates.js";
import { TILES } from "../lib/dashboard-tiles.js";

export default function HomePage() {
  const { session, appUser, loading } = useSession();

  if (loading) {
    return (
      <div style={{ maxWidth: 780, margin: "0 auto", padding: "48px 24px" }}>
        <Wordmark scale={1.6} />
        <p
          style={{
            fontFamily: "var(--ppr-typography-font-family-body)",
            fontSize: "var(--ppr-typography-font-size-md)",
            color: "var(--ppr-color-text-muted)",
            marginTop: 24,
          }}
        >
          Loading…
        </p>
      </div>
    );
  }

  if (session) {
    return <Dashboard session={session} tier={appUser?.tier || "manager"} />;
  }

  return <Navigate to="/signin" replace />;
}

// Warm initials-circle palette — crimson for the most-recent, neutral ink /
// slate for the rest, so the rail reads as a calm row, not a rainbow.
const AVATAR_COLORS = [
  "var(--ppr-color-accent-coral)",
  "var(--ppr-color-text-primary)",
  "var(--ppr-color-text-secondary)",
];

function initials(name) {
  if (!name) return "—";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 1).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

// Build the secondary line for a person card. Defensive against bad data:
// some profiles have a clean role_title ("Business Partner"), others
// have the entire scraped LinkedIn experience dump stuffed into it
// ("Friend · Experience ADP logo ADP Full-time · 1 yr 8 mos Senior...").
// The card has a fixed height + CSS ellipsis already; even truncated
// junk reads ugly. This helper:
//   1. Splits role_title on " · " segments,
//   2. Drops segments that look like LinkedIn scrape noise (Experience
//      headers, "1 yr 8 mos" durations, date ranges, location dumps),
//   3. Keeps the first 1-2 clean segments,
//   4. Falls back to department if role_title is empty / all junk.
//
// Result: Kelly renders as "Friend · Senior Digital Sales Manager".
// Jim still renders as "Business Partner".
function cleanSubtitle(p) {
  if (!p) return "";
  let raw = (p.role_title || "").trim();
  if (!raw) {
    return (p.department || "").trim();
  }
  // Normalize separator variants to a canonical " · " BEFORE splitting.
  // LinkedIn scrapes use the bullet "•" (U+2022), middle dot "·" (U+00B7),
  // and others interchangeably; the split below only matches "·", so a
  // "•"-delimited dump would never split — the whole string became one
  // segment, got flagged junk, and fell through to the raw fallback.
  raw = raw.replace(/[  \s]*[•·‧・][  \s]*/g, " · ");
  // Pure-junk segments — drop entirely after leading/trailing strip
  // has run. These are LinkedIn headers, raw location dumps, or
  // employment-type markers standing alone.
  const isPureJunk = (s) =>
    /^(Experience\b|Full-time$|Part-time$|Contract$|[A-Z][a-z]+,\s*[A-Z][a-z]+)/i.test(s) ||
    /^\d+\s*(yr|yrs|mo|mos)\s*$/i.test(s) || // "3 yrs" alone
    / logo /i.test(s); // "ADP logo ADP" company-logo dumps
  // Strip leading duration prefixes ("1 yr 8 mos Senior..." → "Senior...")
  // so the actual role name underneath becomes visible.
  const stripLeadingJunk = (s) =>
    s
      .replace(/^\d+\s*(yr|yrs|mo|mos)\s+\d+\s*(yr|yrs|mo|mos)\s+/i, "")
      .replace(/^\d+\s*(yr|yrs|mo|mos)\s+/i, "")
      .trim();
  // Trailing-junk patterns — strip from a segment's END to recover the
  // role name. E.g. "Senior Digital Sales Manager Dec 2025 - Present"
  // → "Senior Digital Sales Manager".
  const stripTrailingJunk = (s) =>
    s
      .replace(/\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}\s*-\s*(Present|(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{4}).*$/i, "")
      .replace(/\s+\d+\s*(yr|yrs|mo|mos)\b.*$/i, "")
      .replace(/\s+·.*$/, "") // drop nested " · ..." tails
      .trim();
  const segments = raw
    .split(/\s+·\s+/)
    .map((s) => s.trim())
    .map(stripLeadingJunk)
    .map(stripTrailingJunk)
    .filter((s) => s.length > 0 && !isPureJunk(s));
  // Dedupe consecutive identical segments (LinkedIn often repeats role).
  const deduped = [];
  for (const s of segments) {
    if (deduped[deduped.length - 1] !== s) deduped.push(s);
  }
  const kept = deduped
    .slice(0, 2)
    .map((s) => (s.length > 60 ? s.slice(0, 60) + "…" : s));
  if (kept.length === 0) {
    // Everything filtered as junk — fall back to the first separator chunk
    // (cleaner than a truncated raw dump), else short raw.
    const first = (raw.split(/\s+·\s+/)[0] || raw).trim();
    return first.length > 60 ? first.slice(0, 60) + "…" : first;
  }
  const joined = kept.join(" · ");
  if (p.department && joined.length < 50) {
    return `${joined} · ${p.department}`;
  }
  return joined;
}

function Dashboard({ session, tier }) {
  const navigate = useNavigate();
  const [people, setPeople] = useState([]);
  const [query, setQuery] = useState("");
  const [matches, setMatches] = useState([]);

  // Recent people — the rail. The profiles endpoint returns newest-updated
  // first, so the first handful are the most recently touched.
  useEffect(() => {
    if (!session) return;
    const ctrl = new AbortController();
    fetch("/.netlify/functions/profiles", {
      headers: { authorization: `Bearer ${session.access_token}` },
      signal: ctrl.signal,
    })
      .then((r) => r.json())
      .then((b) => setPeople(b.profiles || []))
      .catch(() => {});
    return () => ctrl.abort();
  }, [session]);

  // Live search over people for the "who are you prepping for?" box.
  useEffect(() => {
    const q = query.trim().toLowerCase();
    if (!q) {
      setMatches([]);
      return;
    }
    setMatches(
      people
        .filter((p) => (p.display_name || "").toLowerCase().includes(q))
        .slice(0, 6),
    );
  }, [query, people]);

  const recent = people.slice(0, 6);

  const onSearchSubmit = (e) => {
    e.preventDefault();
    if (matches[0]) {
      navigate(`/profiles/${matches[0].internal_uuid}`);
    } else if (query.trim()) {
      navigate(`/profiles?search=${encodeURIComponent(query.trim())}`);
    }
  };

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "32px 24px 56px" }}>
      {/* Who are you prepping for? */}
      <div style={{ position: "relative", marginBottom: 36 }}>
        <div style={eyebrowStyle}>Who are you prepping for?</div>
        <form onSubmit={onSearchSubmit}>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search a name, or open a person to build their read…"
            style={{
              width: "100%",
              padding: "13px 16px",
              fontFamily: "var(--ppr-typography-font-family-ui)",
              fontSize: "var(--ppr-typography-font-size-base)",
              border: "1px solid var(--ppr-color-border-default)",
              borderRadius: "var(--ppr-radius-sm)",
              background: "var(--ppr-color-background-surface)",
              color: "var(--ppr-color-text-primary)",
            }}
          />
        </form>
        {matches.length > 0 && (
          <div style={matchDropStyle}>
            {matches.map((p) => (
              <Link key={p.internal_uuid} to={`/profiles/${p.internal_uuid}`} style={matchRowStyle}>
                <span style={{ fontFamily: "var(--ppr-typography-font-family-display)", fontSize: 15 }}>
                  {p.display_name || "—"}
                </span>
                <span style={{ fontFamily: "var(--ppr-typography-font-family-ui)", fontSize: 12.5, color: "var(--ppr-color-text-secondary)" }}>
                  {p.role_title || ""}
                </span>
              </Link>
            ))}
          </div>
        )}
        <div style={{ marginTop: 10, fontFamily: "var(--ppr-typography-font-family-ui)", fontSize: 13 }}>
          <Link to="/profiles/new" style={{ color: "var(--ppr-color-accent-coral)", textDecoration: "none" }}>
            + Build a new read from a name &amp; date of birth →
          </Link>
        </div>
      </div>

      {/* Recent people rail */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 12 }}>
        <span style={sectionTitleStyle}>Recent people</span>
        <Link to="/profiles" style={{ fontFamily: "var(--ppr-typography-font-family-ui)", fontSize: 12.5, color: "var(--ppr-color-accent-coral)", textDecoration: "none" }}>
          All people →
        </Link>
      </div>

      {recent.length === 0 ? (
        <div style={emptyRailStyle}>
          No people yet. Start one — enter a name and date of birth and the full read generates instantly.
          <div style={{ marginTop: 10 }}>
            <Link to="/profiles/new" style={{ color: "var(--ppr-color-accent-coral)", textDecoration: "none" }}>
              + Add your first person →
            </Link>
          </div>
        </div>
      ) : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginBottom: 38 }}>
          {recent.map((p, i) => (
            <Link key={p.internal_uuid} to={`/profiles/${p.internal_uuid}`} style={personCardStyle}>
              <div style={{ ...avatarStyle, background: AVATAR_COLORS[i % AVATAR_COLORS.length] }}>
                {initials(p.display_name)}
              </div>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontFamily: "var(--ppr-typography-font-family-display)", fontSize: 16, color: "var(--ppr-color-text-primary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {p.display_name || "—"}
                </div>
                <div style={{ fontFamily: "var(--ppr-typography-font-family-ui)", fontSize: 11.5, color: "var(--ppr-color-text-secondary)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                  {cleanSubtitle(p)}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}

      {/* Surfaces — demoted to a quiet secondary row */}
      <div style={{ ...sectionTitleStyle, marginBottom: 12 }}>Or engineer an approach</div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {TILES.map((tile) => {
          const allowed = tile.featureKey ? canAccess(tier, tile.featureKey) : true;
          return (
            <Link
              key={tile.key}
              to={tile.to}
              style={{
                fontFamily: "var(--ppr-typography-font-family-ui)",
                fontSize: 13,
                color: allowed ? "var(--ppr-color-text-primary)" : "var(--ppr-color-text-muted)",
                background: "var(--ppr-color-background-surface)",
                border: "1px solid var(--ppr-color-border-default)",
                borderRadius: "var(--ppr-radius-pill)",
                padding: "8px 15px",
                textDecoration: "none",
                display: "inline-flex",
                alignItems: "center",
                gap: 7,
              }}
            >
              {tile.title}
              {tile.isNew && (
                <span style={{ fontSize: 9, letterSpacing: "0.1em", color: "var(--ppr-color-accent-coral)" }}>NEW</span>
              )}
            </Link>
          );
        })}
      </div>

      {/* Footer nav */}
      <div style={footerNavStyle}>
        <Link to="/profiles" style={dashLinkStyle}>People</Link>
      </div>
    </div>
  );
}

const eyebrowStyle = {
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: 11,
  letterSpacing: "0.16em",
  textTransform: "uppercase",
  color: "var(--ppr-color-accent-coral)",
  marginBottom: 10,
};

const sectionTitleStyle = {
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-md)",
  color: "var(--ppr-color-text-primary)",
};

const matchDropStyle = {
  position: "absolute",
  left: 0,
  right: 0,
  zIndex: 50,
  marginTop: 4,
  background: "var(--ppr-color-background-surface)",
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: "var(--ppr-radius-sm)",
  overflow: "hidden",
};

const matchRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "10px 14px",
  textDecoration: "none",
  color: "var(--ppr-color-text-primary)",
  borderBottom: "1px solid var(--ppr-color-border-subtle)",
};

const personCardStyle = {
  display: "flex",
  alignItems: "center",
  gap: 12,
  padding: "14px 16px",
  background: "var(--ppr-color-background-surface)",
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: "var(--ppr-radius-sm)",
  textDecoration: "none",
  color: "inherit",
  minWidth: 0, // grid item must be able to shrink below content width, else a long
               // non-wrapping subtitle blows the 1fr track out past the viewport
};

const avatarStyle = {
  width: 36,
  height: 36,
  flexShrink: 0,
  borderRadius: "50%",
  color: "var(--ppr-color-text-inverse)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: 15,
};

const emptyRailStyle = {
  padding: "20px 22px",
  background: "var(--ppr-color-background-surface)",
  border: "1px solid var(--ppr-color-border-default)",
  borderRadius: "var(--ppr-radius-sm)",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: 14,
  color: "var(--ppr-color-text-secondary)",
  marginBottom: 38,
};

const footerNavStyle = {
  marginTop: 40,
  paddingTop: 18,
  borderTop: "1px solid var(--ppr-color-border-default)",
  display: "flex",
  flexWrap: "wrap",
  gap: 24,
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-sm)",
};

const dashLinkStyle = {
  color: "var(--ppr-color-text-secondary)",
  textDecoration: "none",
};
